// Performance-Schalter und Zusammenlegen identischer Anfragen fuer die AI Suite.
//
// Hintergrund (gemessen am Dashboard eines grossen Projekts): von 22 Anfragen beim Oeffnen
// waren 9 exakte Kopien — mehrere Watcher loesen dieselbe Abfrage im selben Moment aus, und
// in den Tabs geht fast jede Anfrage doppelt raus. Jede Kopie kostet am Server volle Arbeit
// und verstopft den kleinen Verbindungspool.
//
// Was hier passiert: laeuft zu einer LESENDEN AI-Suite-Route bereits eine Anfrage mit exakt
// demselben Body, bekommt der zweite Aufrufer deren Antwort (als Kopie), statt eine neue
// Anfrage zu starten. Es wird nichts ueber das Ende der Anfrage hinaus gemerkt — veraltete
// Daten sind dadurch ausgeschlossen. Schreibende Routen sind nie betroffen.
//
// RUECKGAENGIG MACHEN, ohne Code anzufassen: in der Browser-Konsole
//     localStorage.setItem("quaro_ai_perf", "off")     // aus (danach Seite neu laden)
//     localStorage.removeItem("quaro_ai_perf")          // wieder an
// Dauerhaft fuer alle: AI_PERF_DEFAULT unten auf false setzen.
// Der Schalter steuert auch das Merken der Plattform-Reihen und die Sammelroute im Dashboard
// (AIProject.vue) sowie die Quellen-Zaehlung im Backend (applyPerfFlags unten), also alle
// Massnahmen zusammen.
export const AI_PERF_DEFAULT = true

export function aiPerfEnabled() {
    try {
        const flag = localStorage.getItem("quaro_ai_perf")
        if (flag === "off") return false
        if (flag === "on") return true
    } catch (e) {
        // localStorage nicht verfuegbar (z. B. privater Modus) — Default gilt
    }
    return AI_PERF_DEFAULT
}

// Nur Routen, die ausschliesslich lesen. Alles mit save / set / delete / modify / create /
// update / reset fehlt hier absichtlich: zwei identische Schreibvorgaenge sind zwei Vorgaenge.
const SHAREABLE_PATHS = [
    "/ai-suite/promptresponses/get",
    "/ai-suite/promptresponses/by-brand",
    "/ai-suite/brandvisibility/by/date",
    "/ai-suite/brandvisibility/by/date/batch",
    "/ai-suite/brandvisibility/history",
    "/ai-suite/brandvisibility/range-averages",
    "/ai-suite/brandmetrics",
    "/ai-suite/brand/metrics-range",
    "/ai-suite/sources/get",
    "/ai-suite/sources/urls/get",
    "/ai-suite/sources/page-type-stats/get",
    "/ai-suite/fanout/queries",
    "/ai-suite/shopping/overview",
    "/ai-suite/shopping/providers",
    "/ai-suite/shopping/items",
    "/ai-suite/shopping/queries",
    "/ai-suite/shopping/by-prompt",
    "/ai-suite/tags/list",
    "/ai-suite/brands/all/frontend/get",
    "/ai-suite/brands/edited/frontend/get",
    "/ai-suite/brands/favorites/get",
    "/ai-suite/get/google/promptcount",
    "/ai-suite/opportunities/prompt-gaps",
    "/ai-suite/opportunities/prompt-gaps/detail"
]

export function isShareableUrl(url) {
    if (typeof url !== "string") return false
    const path = url.split("?")[0]
    return SHAREABLE_PATHS.some((p) => path.endsWith(p))
}

// Ist der Schalter aus, soll auch das Backend den bisherigen Weg gehen: die Quellen-Zaehlung
// in brandvisibility/by/date laeuft dann wieder ueber den alten Scan statt ueber die schlanke
// Collection (Body-Feld legacySources). Schalter an = Body bleibt unveraendert.
const LEGACY_SOURCES_PATHS = ["/ai-suite/brandvisibility/by/date", "/ai-suite/brandvisibility/by/date/batch"]

export function applyPerfFlags(url, data) {
    if (aiPerfEnabled() || typeof url !== "string") return data
    if (!data || typeof data !== "object" || Array.isArray(data)) return data
    const path = url.split("?")[0]
    if (!LEGACY_SOURCES_PATHS.some((p) => path.endsWith(p))) return data
    return { ...data, legacySources: true }
}

// Schluessel fuer "dieselbe Anfrage": URL plus kompletter Body. null = nicht zusammenlegen.
export function shareKey(url, data) {
    if (!aiPerfEnabled() || !isShareableUrl(url)) return null
    try {
        return url + "\u0000" + JSON.stringify(data === undefined ? null : data)
    } catch (e) {
        return null
    }
}

// Der zweite Aufrufer bekommt eine eigene Kopie der Daten: manche Aufrufer veraendern das
// Antwortobjekt, und das darf den ersten nicht treffen.
export function cloneResponse(response) {
    if (!response || typeof response !== "object") return response
    let data = response.data
    try {
        data = data === undefined ? data : JSON.parse(JSON.stringify(data))
    } catch (e) {
        // nicht serialisierbar — dann eben dieselbe Referenz
    }
    return { ...response, data }
}

const inflight = new Map()

// Fuehrt `start()` aus oder haengt sich an die laufende identische Anfrage.
export function shareInflight(key, start) {
    if (!key) return start()
    const running = inflight.get(key)
    if (running) return running.then(cloneResponse)
    const promise = start()
    inflight.set(key, promise)
    const release = () => {
        if (inflight.get(key) === promise) inflight.delete(key)
    }
    promise.then(release, release)
    return promise
}

export function inflightCount() {
    return inflight.size
}
